import React from 'react';
import { Skeleton } from '@mui/material';
import companyStyles from '@/pages/Company/company.module.css';

interface CompanyAnalysisLoadingStateProps {
  studyName?: string;
  rowCount?: number;
}

const CompanyAnalysisLoadingState: React.FC<CompanyAnalysisLoadingStateProps> = ({
  studyName = '',
  rowCount = 8,
}) => {
  return (
    <>
      <h1 className={companyStyles.header}>{studyName}</h1>
      <div className={companyStyles.skeletonWrapper}>
        {/* Table */}
        <div className={companyStyles.skeletonTable}>
          {/* Year Groups Header */}
          <div className={companyStyles.skeletonTableHeader}>
            <div className={companyStyles.skeletonCell} style={{ width: '280px' }} />{' '}
            {/* Company + Action */}
            <div className={companyStyles.skeletonCellGroup} style={{ flex: 1 }}>
              <Skeleton variant="text" width={100} height={24} /> {/* 2023 */}
            </div>
            <div className={companyStyles.skeletonCellGroup} style={{ flex: 1 }}>
              <Skeleton variant="text" width={100} height={24} /> {/* 2024 */}
            </div>
            <div className={companyStyles.skeletonCellGroup} style={{ flex: 1 }}>
              <Skeleton variant="text" width={100} height={24} /> {/* YOY */}
            </div>
          </div>

          {/* Column Headers */}
          <div className={companyStyles.skeletonTableRow}>
            {/* Company + Action */}
            <div className={companyStyles.skeletonCell} style={{ width: '230px' }}>
              <Skeleton variant="text" width={100} height={24} />
            </div>
            <div className={companyStyles.skeletonCell} style={{ width: '50px' }}>
              <Skeleton variant="text" width={40} height={24} />
            </div>

            {/* 2023 Columns */}
            <div className={companyStyles.skeletonCellGroup}>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={70} height={24} />
              </div>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={80} height={24} />
              </div>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={50} height={24} />
              </div>
            </div>

            {/* 2024 Columns */}
            <div className={companyStyles.skeletonCellGroup}>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={70} height={24} />
              </div>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={80} height={24} />
              </div>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={50} height={24} />
              </div>
            </div>

            {/* YOY Columns */}
            <div className={companyStyles.skeletonCellGroup}>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={100} height={24} />
              </div>
              <div className={companyStyles.skeletonCell}>
                <Skeleton variant="text" width={100} height={24} />
              </div>
            </div>
          </div>

          {/* Table Rows */}
          {Array(rowCount)
            .fill(null)
            .map((_, index) => (
              <div key={index} className={companyStyles.skeletonTableRow}>
                {/* Company + Action */}
                <div className={companyStyles.skeletonCell} style={{ width: '230px' }}>
                  <Skeleton variant="text" width={180} height={24} />
                </div>
                <div className={companyStyles.skeletonCell} style={{ width: '50px' }}>
                  <Skeleton variant="circular" width={32} height={32} />
                </div>

                {/* 2023 Data */}
                <div className={companyStyles.skeletonCellGroup}>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="text" width={70} height={24} />
                  </div>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="rectangular" width={80} height={32} className="rounded" />
                  </div>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="circular" width={32} height={32} />
                  </div>
                </div>

                {/* 2024 Data */}
                <div className={companyStyles.skeletonCellGroup}>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="text" width={70} height={24} />
                  </div>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="rectangular" width={80} height={32} className="rounded" />
                  </div>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="circular" width={32} height={32} />
                  </div>
                </div>

                {/* YOY Data */}
                <div className={companyStyles.skeletonCellGroup}>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="text" width={100} height={24} />
                  </div>
                  <div className={companyStyles.skeletonCell}>
                    <Skeleton variant="text" width={100} height={24} />
                  </div>
                </div>
              </div>
            ))}

          {/* Pagination */}
          <div className={companyStyles.skeletonPagination}>
            <Skeleton variant="text" width={100} height={24} />
            <div className={companyStyles.skeletonPaginationControls}>
              <Skeleton variant="rectangular" width={32} height={32} className="rounded" />
              <Skeleton variant="rectangular" width={32} height={32} className="rounded" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyAnalysisLoadingState;
